import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

export interface ExtPillarPerformancePopupUserMetric {
    pillarID: number;
    userID: number;
    userName: string;
    superGroupID: number;
    superGroupName: string;
    typeID: number;
    metricID: number;
    metricName: string;
    actual: number;
    variance: number;
    thresholdColor: string;
    trendImage: string;
    displayOrder: number;
}

export interface ExtPillarPerformancePopupItem {
    pillarID: number;
    pillarName: string;
    eventDate: string;
    isQuarter: number;
    metricsPerPillar: number;
    userMetrics: ExtPillarPerformancePopupUserMetric[];
}

export interface PillarPerformancePopupMetric {
    name: string;
}

export interface PillarPerformancePopupUser {
    id: number;
    username: string;
    superGroupName: string;
    values: any[][];
}

export interface PillarPerformancePopupItem {
    id: number;
    name: string;
    metrics: PillarPerformancePopupMetric[];
    users: PillarPerformancePopupUser[];
    overall: PillarPerformancePopupUser;
}

export interface PillarPerformancePopup {
    items: PillarPerformancePopupItem[];
}

class PillarPerformancePopupMetricModelTransformer
    implements
        ModelTransformer<
            PillarPerformancePopupMetric,
            ExtPillarPerformancePopupUserMetric
        >
{
    transform(
        model: ExtPillarPerformancePopupUserMetric,
    ): PillarPerformancePopupMetric {
        return {
            name: model.metricName,
        };
    }
}

class PillarPerformancePopupMetricModelSetTransformer extends ModelSetTransformer<
    PillarPerformancePopupMetric,
    ExtPillarPerformancePopupUserMetric
> {
    constructor(private readonly _numMetrics: number) {
        super();
    }

    protected newTransformer(): ModelTransformer<
        PillarPerformancePopupMetric,
        ExtPillarPerformancePopupUserMetric
    > {
        return new PillarPerformancePopupMetricModelTransformer();
    }

    transform(
        model: ExtPillarPerformancePopupUserMetric[],
    ): PillarPerformancePopupMetric[] {
        const transformer = this.newTransformer();
        const transformed: PillarPerformancePopupMetric[] = [];
        if (model.length < this._numMetrics) return transformed;
        for (let i = 0; i < this._numMetrics; i++) {
            const item = model[i];
            transformed.push(transformer.transform(item));
        }
        return transformed;
    }
}

class PillarPerformancePopupUserModelTransformer
    implements
        ModelTransformer<
            PillarPerformancePopupUser,
            ExtPillarPerformancePopupUserMetric
        >
{
    transform(
        model: ExtPillarPerformancePopupUserMetric,
    ): PillarPerformancePopupUser {
        return {
            id: model.userID,
            username: model.userName,
            superGroupName: model.superGroupName,
            values: [],
        };
    }
}

class PillarPerformancePopupUserModelSetTransformer extends ModelSetTransformer<
    PillarPerformancePopupUser,
    ExtPillarPerformancePopupUserMetric
> {
    constructor(private readonly _numMetrics: number) {
        super();
    }

    protected newTransformer(): ModelTransformer<
        PillarPerformancePopupUser,
        ExtPillarPerformancePopupUserMetric
    > {
        return new PillarPerformancePopupUserModelTransformer();
    }

    transform(
        model: ExtPillarPerformancePopupUserMetric[],
    ): PillarPerformancePopupUser[] {
        const transformer = this.newTransformer();
        const transformed: PillarPerformancePopupUser[] = [];
        let transformedItem;
        model.forEach((item, index) => {
            const metricIndex = index % this._numMetrics;
            if (metricIndex === 0) {
                transformedItem = transformer.transform(item);
            }
            transformedItem.values.push({
                actual: item.actual,
                variance: item.variance,
                thresholdColor: item.thresholdColor.trim(),
                trendClass: item.trendImage,
            });
            if (metricIndex === this._numMetrics - 1) {
                transformed.push(transformedItem);
            }
        });
        return transformed;
    }
}

class PillarPerformancePopupItemModelTransformer
    implements
        ModelTransformer<
            PillarPerformancePopupItem,
            ExtPillarPerformancePopupItem
        >
{
    transform(
        model: ExtPillarPerformancePopupItem,
    ): PillarPerformancePopupItem {
        const metrics = new PillarPerformancePopupMetricModelSetTransformer(
            model.metricsPerPillar,
        ).transform(model.userMetrics);

        const users_ = model.userMetrics.filter((value) => value.userID > 0);
        const users = new PillarPerformancePopupUserModelSetTransformer(
            model.metricsPerPillar,
        ).transform(users_);

        const overall_ = model.userMetrics.filter((value) => value.userID <= 0);
        let overall;
        if (overall_.length) {
            overall = new PillarPerformancePopupUserModelSetTransformer(
                model.metricsPerPillar,
            ).transform(overall_)[0];
        }

        return {
            id: model.pillarID,
            name: model.pillarName,
            metrics,
            users,
            overall,
        };
    }
}

class PillarPerformancePopupItemModelSetTransformer extends ModelSetTransformer<
    PillarPerformancePopupItem,
    ExtPillarPerformancePopupItem
> {
    protected newTransformer(): ModelTransformer<
        PillarPerformancePopupItem,
        ExtPillarPerformancePopupItem
    > {
        return new PillarPerformancePopupItemModelTransformer();
    }
}

export class PillarPerformancePopupModelTransformer
    implements
        ModelTransformer<
            PillarPerformancePopup,
            ExtPillarPerformancePopupItem[]
        >
{
    transform(model: ExtPillarPerformancePopupItem[]): PillarPerformancePopup {
        const items =
            new PillarPerformancePopupItemModelSetTransformer().transform(
                model,
            );
        return {
            items,
        };
    }
}

export function validatePillarPerformancePopup(
    model: PillarPerformancePopup,
): void {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const valid =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model != null && model.items != null && model.items.length !== 0;
    if (!valid) {
        throw new Error('PillarPerformancePopup not valid');
    }
}
